import * as HLF from "html-living-framework";

import { SliderMarker } from "./SliderMarker";
import { ConstrainedDraggable } from "./ConstrainedDraggable";
import Settings from "Data/Settings";
import { TimelineLite } from "gsap";
import * as Topics from "Data/Topics";
import * as Constants from "Data/Constants";

export class Slider extends PIXI.Container implements HLF.Input.IFocusable {
    protected readonly _bg: PIXI.Sprite;
    protected readonly _markers: SliderMarker[];
    protected readonly _knob: ConstrainedDraggable;
    protected _hoverVO: string;
    protected _subscriptions: any[] = [];

    protected _progress: number = 0;

    protected dwellTimeline?: TimelineLite;
    protected isCursorDwelling: boolean = false;

    protected _callback?: (pProgress: number) => void;

    public constructor (
        steps: number,
        bgTexture: string,
        callback: (pValue: number) => void,
        pHoverVO: string,
    ) {
        super();

        this.interactive = true;
        this.buttonMode = false;

        this.on(HLF.Input.Events.POINTER_OVER, this.onPointerOver);
        this.on(HLF.Input.Events.POINTER_OUT, this.onPointerOut);

        this.addListener(HLF.Input.Events.CLICK, (e)=>{
            if (Settings.useDwellActivation) {
                this.onFocusActivated();
                this.cancelDwell();
            } // else the marker handles the click
        });

        this._bg = this.addChild(HLF.PixiUtils.makeSprite(bgTexture));

        this._knob = new ConstrainedDraggable (
            "cyberchase-settings-yellow-thumb",
            new PIXI.Point(0.5, 0.5),
            pHoverVO,
            this.onSliderProgress.bind(this),
            this.onKnobActivated.bind(this),
        );
        this._knob.position.x = -this._bg.width / 2;
        this._knob.setEndPoint(new PIXI.Point(this._bg.width / 2, 0));
        this._knob.steps = steps - 1;

        this._markers = [];
        let x = -this._bg.width / 2;

        for (let i=0; i<steps; i++) {
            const marker = this.addChild(new SliderMarker(this._knob, ()=>{
                this.onMarkerClicked(i / (steps - 1));
            }));
            // marker.setLayoutTexture("empty-pixel", 0.5, 0.5);
            // marker.scale.set(0.5);
            marker.position.x = x;
            this._markers.push(marker);
            x += (this._bg.width / (steps - 1));
        }

        this.addChild(this._knob);

        this._callback = callback;
        this._hoverVO = pHoverVO;

        this._bg.hitArea = new PIXI.Rectangle(-this._bg.width / 2 - this._knob.width / 2, -this._knob.height / 2, this._bg.width + this._knob.width, this._knob.height);
    }
    public get knob() { return this._knob; }

    public isFocusable(): boolean {
        return this.worldVisible && this._knob.interactive;
    }

    onFocusBegin(): void {
        this.playKeyboardSelectVO();
        this.knob.onFocusBegin();
    }
    onFocusEnd(): void {
        this.knob.onFocusEnd();
    }
    onFocusActivated(): void {
        this.Increment();
    }
    getFocusPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }
    getFocusSize(): PIXI.IPoint {
        return new PIXI.Point(HLF.PixiUtils.getGlobalBounds(this._bg).width + this._knob.width, this._knob.height);
    }

    public setInteractive(pValue: boolean) {
        this._knob.interactive = pValue;
        if (this._knob.isSelected) {
            this._knob.deselect();
        }
    }

    public destroy(pDestroyOptions?: any) {
        super.destroy(pDestroyOptions);
        this._subscriptions.forEach(PubSub.unsubscribe);
    }

    public setValue(pValue: number) {
        if (!this._knob.isDragging) {
            this._knob.setProgress(pValue, {dragging: false});
        }
        this._progress = pValue;
    }

    public Increment() {
        let newValue: number;
        if (this._progress >= 1) {
            newValue = 0;
        }
        else {
            newValue = (Math.round(this._progress * this._knob.steps) + 1) / (this._knob.steps);
        }
        this.onMarkerClicked(newValue);
    }


    protected onPointerOver(pEvent: PIXI.InteractionEvent): void {
        this.playHoverVO();
        if (Settings.useDwellActivation) {
            this.beginDwell();
        }
    }

    protected onPointerOut(pEvent: PIXI.InteractionEvent): void {
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
    }

    protected onMarkerClicked(pValue: number) {
        this._knob.setProgress(pValue, {dragging: false});
        this.onSliderProgress(pValue);
    }

    protected onKnobActivated(): void {
        if (Settings.useDwellActivation === false) {
            this.Increment();
        }
    }

    protected onSliderProgress(pProgress: number) {
        this._progress = pProgress;
        this._callback?.(pProgress);
    }

    protected playHoverVO(): void {
        HLF.Application.instance.voiceover.playVO(this._hoverVO, { priority: -1, mode: HLF.PlayMode.Override});
    }


    protected playKeyboardSelectVO(): void {
        HLF.Application.instance.voiceover.stopVO();
        HLF.Application.instance.voiceover.playVO(this._hoverVO, { priority: 0, mode: HLF.PlayMode.Override, callback: (pDidPlay)=>{
            if (Settings.audioDescriptionEnabled) {
                this._callback?.(this._progress);
            }
        }});
    }

    protected beginDwell() {
        this.isCursorDwelling = true;
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        this.dwellTimeline = new TimelineLite();
        this.dwellTimeline.call(()=>PubSub.publishSync(Topics.DWELL_BEGIN, this), undefined, undefined, 0);
        this.dwellTimeline.call(()=>this.completeDwell(), undefined, undefined, Constants.DWELL_DURATION);
    }

    protected cancelDwell(): void {
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        if (this.isCursorDwelling) {
            this.isCursorDwelling = false;
            PubSub.publishSync(Topics.DWELL_CANCELLED, this);
        }
    }

    protected completeDwell(): void {
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        PubSub.publishSync(Topics.DWELL_ACTIVATED, this);
        this.isCursorDwelling = false;
        this.onFocusActivated(); // TODO: swap texture to down texture?
    }

}